// The playbooks rail beside the agenda, before and during a call (Adam,
// 2026-10-31): his scripts and playbooks, kept as Seamless notes, as a list of
// titles. One opens large and set for reading aloud; Edit makes the same text
// editable in place, and it saves when the text loses focus, when he goes back
// or opens another, after a pause in typing, and when the page hides. Which one
// was open is remembered per call. Host only: PrepWorkspace renders this only
// for the signed-in operator, and the Worker refuses everyone else.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type JSX, type ReactNode } from "react";
import { parseBody, readOpen, writeOpen, type Block, type Playbook, type Span } from "../lib/playbooks";
import { edit as recordEdit, refresh, saver, snapshot, subscribe, wirePageHide } from "../lib/playbooksStore";
import { BackIcon, ChevronIcon } from "./icons";

const IDLE_SAVE_MS = 2500;
const POLL_MS = 3000;

const EDITABLE: "plaintext-only" | "true" = (() => {
  try {
    const d = document.createElement("div");
    d.contentEditable = "plaintext-only";
    return d.contentEditable === "plaintext-only" ? "plaintext-only" : "true";
  } catch {
    return "true";
  }
})();

function storage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

interface PlaybookRailProps {
  /** The call's prep slug: which playbook was open is remembered per call. */
  readonly callSlug: string | null;
  /** The pane is folded away; opening it again refreshes the list. */
  readonly shut: boolean;
  /** The pane's fold button, set at the end of the bar. */
  readonly fold: ReactNode;
}

export function PlaybookRail({ callSlug, shut, fold }: PlaybookRailProps): JSX.Element {
  const state = useSyncExternalStore(subscribe, snapshot);
  const [openId, setOpenId] = useState<string | null>(() => readOpen(storage(), callSlug));
  const [editing, setEditing] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const idle = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    wirePageHide();
  }, []);

  // A change made in Seamless, or on another screen, shows here within seconds
  // while the call is on: poll while the pane is open and the page is visible,
  // and ask again the moment the page comes back to the front.
  useEffect(() => {
    if (shut) return;
    void refresh();
    const ask = (): void => {
      if (!document.hidden) void refresh();
    };
    const timer = setInterval(ask, POLL_MS);
    document.addEventListener("visibilitychange", ask);
    window.addEventListener("focus", ask);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", ask);
      window.removeEventListener("focus", ask);
    };
  }, [shut]);

  useEffect(() => {
    setOpenId(readOpen(storage(), callSlug));
  }, [callSlug]);

  const list = state.kind === "ready" ? state.list : null;
  const open: Playbook | null = (openId && list?.find((p) => p.id === openId)) || null;

  // Whatever is typed goes to the saver; nothing typed is held only by the page.
  const commit = useCallback((): void => {
    if (idle.current) {
      clearTimeout(idle.current);
      idle.current = null;
    }
    const el = textRef.current;
    const id = el?.dataset.id;
    if (!el || !id || el.contentEditable === "false" || el.contentEditable === "inherit") return;
    recordEdit(id, el.innerText);
  }, []);

  const commitRef = useRef(commit);
  commitRef.current = commit;
  useEffect(() => () => commitRef.current(), []);

  const choose = (id: string | null): void => {
    commit();
    setEditing(false);
    setOpenId(id);
    writeOpen(storage(), callSlug, id);
  };

  // Editing shows the plain text. It is set by hand, once when Edit opens,
  // never by React, so a refresh cannot move the caret.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || !open || !editing) return;
    el.dataset.id = open.id;
    el.textContent = open.body;
  }, [open?.id, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const blocks = useMemo(() => (open ? parseBody(open.body, { firstIsTitle: true }) : []), [open?.body]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!editing) return;
    const el = textRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    const sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [editing]);

  const onInput = (): void => {
    const el = textRef.current;
    const id = el?.dataset.id;
    if (!el || !id) return;
    saver.edit(id, el.innerText, list?.find((p) => p.id === id)?.body ?? "");
    if (idle.current) clearTimeout(idle.current);
    idle.current = setTimeout(() => {
      idle.current = null;
      void saver.flush();
    }, IDLE_SAVE_MS);
  };

  const finish = (): void => {
    commit();
    setEditing(false);
  };

  const failed = saver.status === "failed" && open !== null && saver.pendingBody(open.id) !== undefined;
  const saving = saver.status === "saving" && open !== null && saver.pendingBody(open.id) !== undefined;

  return (
    <>
      <header className="pw__bar pb__bar">
        {open ? (
          <>
            <button type="button" className="pb__back" aria-label="All playbooks" onClick={() => choose(null)}>
              <BackIcon size={20} />
            </button>
            <h2 className="pb__title">{open.title}</h2>
            {saving ? <span className="pb__status">Saving</span> : null}
            <button
              type="button"
              className="pb__edit"
              aria-pressed={editing}
              // Keep focus in the text so its blur does not race the tap.
              onPointerDown={(e) => {
                if (editing) e.preventDefault();
              }}
              onClick={() => (editing ? finish() : setEditing(true))}
            >
              {editing ? "Done" : "Edit"}
            </button>
          </>
        ) : (
          <span className="pw__label">Playbooks</span>
        )}
        {fold}
      </header>
      {failed && !shut ? (
        <div className="pb__failed" role="status">
          <p>Not saved. Retrying</p>
          <button type="button" className="pb__retry" onClick={() => void saver.flush()}>
            Try again
          </button>
        </div>
      ) : null}
      <div className="pw__body" hidden={shut}>
        {open ? (
          <div className="pb__read">
            {editing ? (
              <div
                ref={textRef}
                key={`edit-${open.id}`}
                data-id={open.id}
                className="pb__text pb__text--editing"
                contentEditable={EDITABLE}
                suppressContentEditableWarning
                role="textbox"
                aria-multiline
                aria-label={open.title}
                spellCheck
                onInput={onInput}
                onBlur={finish}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    (e.currentTarget as HTMLElement).blur();
                  }
                }}
              />
            ) : (
              <div key={`read-${open.id}`} className="pb__text pb__fmt">
                {blocks.map((b, i) => (
                  <PlaybookBlock key={i} block={b} />
                ))}
              </div>
            )}
            {!editing && open.body.trim() === "" ? <p className="pb__blank">This playbook is empty. Edit to write it.</p> : null}
          </div>
        ) : (
          <BookList state={state} current={openId} onOpen={(id) => choose(id)} />
        )}
      </div>
    </>
  );
}

function Runs({ spans }: { readonly spans: readonly Span[] }): JSX.Element {
  return (
    <>
      {spans.map((s, i) =>
        s.bold ? (
          <strong key={i}>{s.italic ? <em>{s.text}</em> : s.text}</strong>
        ) : s.italic ? (
          <em key={i}>{s.text}</em>
        ) : (
          <span key={i}>{s.text}</span>
        ),
      )}
    </>
  );
}

function PlaybookBlock({ block }: { readonly block: Block }): JSX.Element {
  if (block.kind === "gap") return <div className="pb__gap" aria-hidden="true" />;
  if (block.kind === "heading") {
    return (
      <h3 className={`pb__h pb__h--${block.level}`}>
        <Runs spans={block.spans} />
      </h3>
    );
  }
  if (block.kind === "item") {
    return (
      <p className={block.depth ? "pb__li pb__li--in" : "pb__li"}>
        <span className="pb__mk" aria-hidden="true">
          {block.marker}
        </span>
        <span className="pb__li-text">
          <Runs spans={block.spans} />
        </span>
      </p>
    );
  }
  return (
    <p className="pb__p">
      <Runs spans={block.spans} />
    </p>
  );
}

function BookList({
  state,
  current,
  onOpen,
}: {
  readonly state: ReturnType<typeof snapshot>;
  readonly current: string | null;
  readonly onOpen: (id: string) => void;
}): JSX.Element {
  if (state.kind === "loading") {
    return (
      <div className="pb__list" aria-busy="true" aria-label="Loading playbooks">
        {[72, 54, 64, 46].map((w) => (
          <div key={w} className="pb__skel">
            <span style={{ width: `${w}%` }} />
          </div>
        ))}
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="pw__empty">
        <p>Could not load the playbooks.</p>
        <button type="button" className="pe__retry" onClick={() => void refresh()}>
          Try again
        </button>
      </div>
    );
  }
  if (state.list.length === 0) return <div className="pw__empty">No playbooks yet.</div>;
  return (
    <ul className="pb__list">
      {state.list.map((p) => (
        <li key={p.id}>
          <button type="button" className={p.id === current ? "pb__row pb__row--on" : "pb__row"} onClick={() => onOpen(p.id)}>
            <span className="pb__rowtitle">{p.title}</span>
            <ChevronIcon size={18} />
          </button>
        </li>
      ))}
    </ul>
  );
}
